import React from 'react';
import {
  Box, CircularProgress, CssBaseline, ThemeProvider,
} from '@mui/material';
import { Route, Routes } from 'react-router-dom';
import { AUTH_STATUSES, ROUTES } from '../../consts';
import { AccountAssociatedNewPage } from '../../pages/AccountAssociatedNewPage';
import { useAuth } from '../../hooks/useAuth';
import { MatchEditPage } from '../../pages/MatchEditPage';
import LoginPage from '../../pages/LoginPage';
import PositionPage from '../../pages/PositionPage';
import AccountsPage from '../../pages/AccountsPage';
import PositionDetailsPage from '../../pages/PositionDetailsPage';
import Layout from '../Layout';
import ProtectedRoute from '../ProtectedRoute';
import { createTheme } from '../../theme';
import { TalentEditPage } from '../../pages/TalentEditPage';
import { TalentNewPage } from '../../pages/TalentNewPage';
import { TalentsListPage } from '../../pages/TalentsListPage';
import { MatchPreviewPage } from '../../pages/MatchPreviewPage';
import HomePage from '../../pages/HomePage';
import { WithPermission } from '../WithPermission';
import { PERMISSIONS, PERMISSIONS_GROUPS } from '../../consts/permissions';
import { SearchAccountPage } from '../../pages/SearchAccountPage/SearchAccount.page';
import { CreateAccountPage } from '../../pages/CreateAccountPage/CreateAccount.page';
import { AiAppPage } from '../../pages/AiAppPage';
import { ChoosePlanPage } from '../../pages/ChoosePlanPage/ChoosePlan.page';
import ReferralsPage from '../../pages/ReferralsPage';

const style = {
  loader: {
    sx: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      textAlign: 'center',
      minHeight: '100vh',
    },
  },
} as const;

const App = (): JSX.Element => {
  const { status } = useAuth();
  const theme = createTheme({
    mode: 'light',
  });
  const router = () => {
    if (status !== AUTH_STATUSES.INITIALIZE) {
      return (
        <Layout>
          <Routes>
            <Route path={ ROUTES.HOME } element={ <ProtectedRoute /> }>
              <Route path={ ROUTES.HOME } element={ <HomePage /> } />
              <Route
                path={ ROUTES.SALES.SEARCH_ACCOUNT }
                element={ (
                  <WithPermission
                    permissions={ [PERMISSIONS.SALES_FLOW] }
                  >
                    <SearchAccountPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.SALES.CREATE_ACCOUNT }
                element={ (
                  <WithPermission
                    permissions={ [PERMISSIONS.SALES_FLOW] }
                  >
                    <CreateAccountPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.SALES.CHOOSE_PLAN }
                element={ (
                  <WithPermission
                    permissions={ [PERMISSIONS.SALES_FLOW] }
                  >
                    <ChoosePlanPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.POSITIONS }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.POSITION_VIEW] }>
                    <PositionPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.POSITION_MATCHES }
                element={ (
                  <WithPermission
                    permissions={ PERMISSIONS_GROUPS.POSITION_MATCHES_LIST_PAGE }
                  >
                    <PositionDetailsPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.TALENTS }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.TALENT_VIEW] }>
                    <TalentsListPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.TALENT_VIEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.TALENT_VIEW] }>
                    <TalentEditPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.TALENTS_NEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.TALENT_CREATE] }>
                    <TalentNewPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.ACCOUNTS }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.ACCOUNT_VIEW] }>
                    <AccountsPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.ACCOUNT_VIEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.ACCOUNT_VIEW] }>
                    <AccountsPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.ACCOUNTS_NEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.ACCOUNT_CREATE] }>
                    <AccountAssociatedNewPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.MATCH_VIEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.MATCH_VIEW] }>
                    <MatchEditPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.MATCH_PREVIEW }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.MATCH_VIEW] }>
                    <MatchPreviewPage />
                  </WithPermission>
                ) }
              />
              <Route
                path={ ROUTES.REFERRALS }
                element={ (
                  <WithPermission permissions={ [PERMISSIONS.REFERRAL_VIEW] }>
                    <ReferralsPage />
                  </WithPermission>
                ) }
              />
              <Route path={ ROUTES.AI_APP } element={ <AiAppPage /> } />
            </Route>
            <Route path={ ROUTES.LOGIN } element={ <LoginPage /> } />
          </Routes>
        </Layout>
      );
    }

    return (
      <Box sx={ style.loader.sx }>
        <CircularProgress />
      </Box>
    );
  };

  // flex styling is required for match preview page!
  return (
    <div
      style={ { display: 'flex', flexDirection: 'column', minHeight: '100vh' } }
    >
      <ThemeProvider theme={ theme }>
        <CssBaseline />
        {router()}
      </ThemeProvider>
    </div>
  );
};

export default App;
